import { Component } from '@theme/component';
import { debounce, isMobileBreakpoint } from '@theme/utilities';

class ScrollingCards extends Component {
  /** @type {HTMLElement[]} */
  #cardElements = [];

  /** @type {number} */
  #cardCount = 0;

  /** @type {Animation[]} */
  #timelineAnimations = [];

  /** @type {AbortController | null} */
  #scrollListenerAbort = null;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {IntersectionObserver | null} */
  #inViewObserver = null;

  /** @type {boolean} */
  #isInView = true;

  /** @type {boolean} */
  #isAnimatingZone = false;

  /** @type {(() => void) | null} */
  #scheduleUpdate = null;

  /** @type {WeakMap<HTMLElement, string>} */
  #lastTransformByCard = new WeakMap();

  // Layout cache — computed once in #setupScrollFallback() and on resize
  #cardTop = 0;
  #cardHeight = 0;
  #windowHeight = 0;
  #cachedSectionTopDocPx = 0;
  #cachedElementHeight = 0;

  /** Whether we've entered the animation zone and refreshed the position cache. */
  #animationZoneEntered = false;

  #handleResize = debounce(() => {
    for (const card of this.querySelectorAll('.scrolling-card')) {
      card.style.removeProperty('transform');
    }
    this.style.removeProperty('padding-bottom');
    if (this.#scrollRafId != null) cancelAnimationFrame(this.#scrollRafId);
    this.#scrollRafId = null;
    this.#init();
  }, 150);

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => this.#init());
    window.addEventListener('resize', this.#handleResize, { signal: this.abortController.signal });
  }

  updatedCallback() {
    super.updatedCallback();
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    this.#cancelTimelineAnimations();
    this.#scrollListenerAbort?.abort();
    this.#scrollListenerAbort = null;
    this.#inViewObserver?.disconnect();
    this.#inViewObserver = null;
    this.#handleResize.cancel();
    if (this.#scrollRafId != null) cancelAnimationFrame(this.#scrollRafId);
    this.#scrollRafId = null;
    this.#scheduleUpdate = null;
    this.style.removeProperty('padding-bottom');
    super.disconnectedCallback();
  }

  #init() {
    this.#cancelTimelineAnimations();
    this.#scrollListenerAbort?.abort();
    this.#scrollListenerAbort = null;

    this.#cardElements = [...this.querySelectorAll('.scrolling-card')];
    this.#cardCount = this.#cardElements.length;

    if (!this.#cardCount) return;

    // Sticky disabled on mobile: cards render as a plain stacked list, so skip the
    // scroll animation entirely and make sure no leftover transforms remain.
    if (this.#isStickyDisabledOnMobile()) {
      this.#resetCards();
      return;
    }

    if (this.#isViewTimelineAvailable()) {
      try {
        this.#setupViewTimelineAnimations();
        return;
      } catch {
        /* fall through to scroll fallback */
      }
    }

    this.#setupScrollFallback();
  }

  /** True when the merchant disabled sticky on mobile and we're at a mobile viewport (< md). */
  #isStickyDisabledOnMobile() {
    return this.dataset.stickyMobile === 'false' && isMobileBreakpoint();
  }

  /** Clears any inline transforms/z-index/padding left over from the scroll animation. */
  #resetCards() {
    for (const card of this.#cardElements) {
      card.style.removeProperty('transform');
      card.style.removeProperty('z-index');
    }
    this.style.removeProperty('padding-bottom');
    this.#lastTransformByCard = new WeakMap();
  }

  // ---------------------------------------------------------------------------
  // ViewTimeline
  // ---------------------------------------------------------------------------

  #isViewTimelineAvailable() {
    return typeof ViewTimeline !== 'undefined';
  }

  #setupViewTimelineAnimations() {
    const totalCards = this.#cardCount;
    if (!totalCards) return;

    const timeline = new ViewTimeline({ subject: this, axis: 'block' });

    this.#cardElements.forEach((card, cardIndex) => {
      const oneBasedSlot = cardIndex + 1;
      const trailingCardCount = totalCards - oneBasedSlot;
      const startPct = (cardIndex / totalCards) * 100;
      const endPct = (oneBasedSlot / totalCards) * 100;

      const animation = card.animate(
        {
          transform: ['scale(1)', `scale(${1 - 0.1 * trailingCardCount})`],
        },
        {
          timeline,
          fill: 'forwards',
          rangeStart: `exit-crossing ${startPct}%`,
          rangeEnd: `exit-crossing ${endPct}%`,
        }
      );
      this.#timelineAnimations.push(animation);
    });
  }

  #cancelTimelineAnimations() {
    for (const animation of this.#timelineAnimations) {
      try {
        animation.cancel();
      } catch {
        /* ignore */
      }
    }
    this.#timelineAnimations = [];
  }

  // ---------------------------------------------------------------------------
  // Scroll fallback (no ViewTimeline — iOS 16.1, older browsers)
  // Algorithm: scale each card based on pixels scrolled past it.
  // ---------------------------------------------------------------------------

  #setupScrollFallback() {
    this.#windowHeight = window.innerHeight;

    if (this.#cardElements[0]) {
      const cardStyle = getComputedStyle(this.#cardElements[0]);
      this.#cardTop = Math.floor(parseFloat(cardStyle.getPropertyValue('top'))) || 0;
      this.#cardHeight = Math.floor(this.#cardElements[0].offsetHeight) || 0;
    }

    // Cache before padding mutation
    this.#cachedElementHeight = this.offsetHeight;
    const rect = this.getBoundingClientRect();
    this.#cachedSectionTopDocPx = rect.top + window.scrollY;

    this.#cardElements.forEach((card, i) => {
      this.#setCardTransform(card, 'translateZ(0) scale(1)');
      card.style.zIndex = String(i + 1);
    });

    this.#animationZoneEntered = false;
    this.#isAnimatingZone = false;
    this.#setupInViewObserver();
    this.#scrollListenerAbort = new AbortController();
    this.#bindScrollFallback(this.#scrollListenerAbort.signal);
  }

  #setupInViewObserver() {
    this.#inViewObserver?.disconnect();
    this.#inViewObserver = new IntersectionObserver(
      (entries) => {
        this.#isInView = !!entries[0]?.isIntersecting;
        if (this.#isInView) this.#scheduleUpdate?.();
      },
      { threshold: 0, rootMargin: '50% 0px' }
    );
    this.#inViewObserver.observe(this);
  }

  #bindScrollFallback(signal) {
    const scheduleUpdate = () => {
      if (!this.#isInView) return;
      if (this.#scrollRafId != null) return;
      this.#scrollRafId = requestAnimationFrame(() => {
        this.#scrollRafId = null;
        this.#updateTransforms();
      });
    };
    this.#scheduleUpdate = scheduleUpdate;
    scheduleUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
  }

  #setCardTransform(card, transform) {
    if (this.#lastTransformByCard.get(card) === transform) return;
    card.style.transform = transform;
    this.#lastTransformByCard.set(card, transform);
  }

  /**
   * Hot path — uses cached section document position + window.scrollY.
   * One getBoundingClientRect call fires once when first entering the animation
   * zone, correcting for any layout shifts (e.g. scroll-reading-text resizing).
   */
  #updateTransforms() {
    if (!this.#isInView) return;
    const scrollY = window.scrollY;
    let top = this.#cachedSectionTopDocPx - scrollY;

    // Guard: only animate when the stacking zone is active
    const guard =
      this.#cardTop - top +
      this.#windowHeight -
      this.#cachedElementHeight -
      this.#cardHeight;

    if (guard > 0) {
      if (this.#animationZoneEntered) this.#animationZoneEntered = false;
      if (!this.#isAnimatingZone) return;
      this.#isAnimatingZone = false;
      // Restore initial no-offset stack state once when leaving animation zone
      this.#cardElements.forEach((card) => this.#setCardTransform(card, 'translateZ(0) scale(1)'));
      return;
    }

    this.#isAnimatingZone = true;

    // First time entering the animation zone: refresh the position cache once
    // to correct for any layout shifts that happened after init (e.g. other
    // sections setting CSS variables that affect page height).
    if (!this.#animationZoneEntered) {
      this.#animationZoneEntered = true;
      const rect = this.getBoundingClientRect();
      this.#cachedSectionTopDocPx = rect.top + scrollY;
      top = rect.top;
    }

    const lastIndex = this.#cardCount - 1;
    for (let i = 0; i < this.#cardCount; i++) {
      const card = this.#cardElements[i];
      const scrolling = this.#cardTop - top - i * this.#cardHeight;
      const base = 'translateZ(0)';
      if (scrolling > 0) {
        const scaling = i === lastIndex ? 1 : (this.#cardHeight - scrolling * 0.15) / this.#cardHeight;
        const minScale = i === lastIndex ? 1 : 1 - 0.1 * (lastIndex - i);
        const clampedScale = Math.max(minScale, Math.min(1, scaling));
        this.#setCardTransform(card, `${base} scale(${clampedScale})`);
      } else {
        this.#setCardTransform(card, `${base} scale(1)`);
      }
    }
  }
}

if (!customElements.get('scrolling-cards')) {
  customElements.define('scrolling-cards', ScrollingCards);
}
